import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { ILead, IMessage, IStageHistory, ILeadPropertyInterest } from 'src/services/api';
import { EMessageSystemMessageType, type EMessageChannel } from 'src/services/api/messages/types';
import type { IShowing } from 'src/api/landlord-showings-api';
import { ELeadStage } from 'src/services/api/leads/types';
import type { TStageOrigin } from 'src/components/lead-sidebar/lead-stage-timeline-events';
import {
    getStageCopy,
    getUnknownStageCopy,
    getStageOrigin,
    getStageOriginLabel,
    getStageOriginDetails,
    getInterestOrigin,
    getInterestOriginDetails,
} from 'src/components/lead-sidebar/lead-stage-timeline-events';

dayjs.extend(utc);

/**
 * Two clocks reach this module. `landlord_messages.inserted_at` and `showings.start_time` are
 * serialized with an offset; everything from the stage-history endpoint and the tour's
 * `canceled_at`/`confirmed_at` come off naive columns that hold UTC and serialize without one.
 * Parsing the naive ones with plain `dayjs` reads them as local time and drops every event
 * hours away from the message it belongs to.
 */
const utcClock = (iso: string): number => dayjs.utc(iso).valueOf();
const offsetClock = (at: string | Date): number => dayjs(at).valueOf();

const ECHO_WINDOW_MS = 60_000;

const NEGATIVE_STAGES: string[] = [ELeadStage.NOT_QUALIFIED, ELeadStage.NOT_INTERESTED];

export type TThreadEventTone = 'interest' | 'positive' | 'negative';

export type TThreadEventKind = 'interest' | 'stage' | 'tour';

export type TThreadEvent = {
    key: string;
    at: number;
    kind: TThreadEventKind;
    /** Only set for stage events; picks the stage's icon. */
    stageValue?: string;
    /** Only set for tour events; picks the tour icon variant. */
    tourOutcome?: 'cancelled' | 'confirmed' | 'attended' | 'missed';
    label: string;
    detail: string;
    tone: TThreadEventTone;
    origin: TStageOrigin | null;
    originLabel: string | null;
    originDetail: string | null;
};

export type TThreadRow =
    | { type: 'message'; key: string; at: number; message: IMessage; switchedToChannel: EMessageChannel | null }
    | { type: 'event'; key: string; at: number; event: TThreadEvent };

type BuildArgs = {
    lead: ILead;
    messages: IMessage[];
    stages: IStageHistory[];
    interests: ILeadPropertyInterest[];
    tours: IShowing[];
    channelSwitches: (EMessageChannel | null)[];
    leadOwnerName: string;
};

/**
 * The earliest interest is the property the lead arrived on. Every later interest is a real
 * change worth calling out, even when both were recorded before the first conversational
 * message (for example while an ingestion pipeline was still processing the lead).
 */
const buildInterestEvents = (
    lead: ILead,
    interests: ILeadPropertyInterest[]
): TThreadEvent[] => interests
    .map((interest) => {
        const origin = getInterestOrigin(interest.actor);

        return {
            key: `interest-${interest.id}`,
            at: utcClock(interest.created_at),
            kind: 'interest' as const,
            label: `Now interested in ${interest.property_name}`,
            detail: `${lead.name} was matched to ${interest.property_name}`,
            tone: 'interest' as const,
            origin,
            originLabel: origin && getStageOriginLabel(origin),
            originDetail: origin && interest.actor ? getInterestOriginDetails(interest.actor, origin) : null,
        };
    })
    .sort((a, b) => a.at - b.at || a.key.localeCompare(b.key))
    .slice(1);

/**
 * Tours contribute the three moments they actually timestamp. A booking is deliberately not
 * one of them: `showings.inserted_at` is not serialized, and the `tour_scheduled` stage row
 * already covers it.
 *
 * Attendance has no timestamp of its own, so it sits at the tour's start time — which is
 * where a reader expects it anyway; "attended" describes the slot, not the moment someone
 * ticked the box.
 */
const buildTourEvents = (lead: ILead, tours: IShowing[]): TThreadEvent[] => {
    const events: TThreadEvent[] = [];

    for (const tour of tours) {
        if (tour.is_canceled && tour.canceled_at) {
            events.push({
                key: `tour-${tour.id}-cancelled`,
                at: utcClock(tour.canceled_at),
                kind: 'tour',
                tourOutcome: 'cancelled',
                label: 'Tour cancelled',
                detail: `${lead.name}'s tour was cancelled`,
                tone: 'negative',
                origin: null,
                originLabel: null,
                originDetail: null,
            });
        }

        if (tour.is_confirmed && tour.confirmed_at) {
            events.push({
                key: `tour-${tour.id}-confirmed`,
                at: utcClock(tour.confirmed_at),
                kind: 'tour',
                tourOutcome: 'confirmed',
                label: 'Tour confirmed',
                detail: `${lead.name}'s tour was confirmed`,
                tone: 'positive',
                origin: null,
                originLabel: null,
                originDetail: null,
            });
        }

        // A cancelled tour's attendance is meaningless, and stale values do linger on the row.
        if (tour.is_attended !== null && tour.is_attended !== undefined && !tour.is_canceled) {
            events.push({
                key: `tour-${tour.id}-attendance`,
                at: offsetClock(tour.start_time),
                kind: 'tour',
                tourOutcome: tour.is_attended ? 'attended' : 'missed',
                label: tour.is_attended ? 'Tour attended' : 'Tour missed',
                detail: tour.is_attended ?
                    `${lead.name} attended the tour` :
                    `${lead.name} did not attend the tour`,
                tone: tour.is_attended ? 'positive' : 'negative',
                origin: null,
                originLabel: null,
                originDetail: null,
            });
        }
    }

    return events;
};

/**
 * Stage rows the backend writes from a listener on something already in the thread. Printing
 * them alongside their own cause is how you end up with "Lead contacted" beside every reply,
 * so each one is dropped when the thing that produced it sits within a minute of it.
 *
 * `auto_after_showing_inserted` is deliberately absent: nothing in the thread represents a
 * booking, so that row is the only trace of one.
 */
const ECHO_REASONS: Record<string, 'message' | 'tour-cancel'> = {
    auto_after_message_inserted: 'message',
    auto_after_tour_cancelled: 'tour-cancel',
};

type TSystemMessageMoment = {
    at: number;
    type: EMessageSystemMessageType;
};

const SYSTEM_MESSAGE_STAGE: Partial<Record<EMessageSystemMessageType, ELeadStage>> = {
    [EMessageSystemMessageType.APPLICATION_APPROVED]: ELeadStage.APPLICATION_APPROVED,
    [EMessageSystemMessageType.APPLICATION_RECEIVED]: ELeadStage.APPLICATION_COMPLETED,
    [EMessageSystemMessageType.APPLICATION_REJECTED]: ELeadStage.NOT_QUALIFIED,
    [EMessageSystemMessageType.LEASE_SIGNED]: ELeadStage.LEASE_SIGNED,
    [EMessageSystemMessageType.TOUR_CONFIRMED_BY_LEAD]: ELeadStage.TOUR_SCHEDULED,
    [EMessageSystemMessageType.TOUR_CREATED]: ELeadStage.TOUR_SCHEDULED,
    [EMessageSystemMessageType.TOUR_REQUESTED]: ELeadStage.TOUR_REQUESTED,
    [EMessageSystemMessageType.TOUR_UPDATED]: ELeadStage.TOUR_SCHEDULED,
};

const SYSTEM_MESSAGE_TOUR_OUTCOME: Partial<Record<
    EMessageSystemMessageType,
    TThreadEvent['tourOutcome']
>> = {
    [EMessageSystemMessageType.TOUR_CANCELLED]: 'cancelled',
    [EMessageSystemMessageType.TOUR_CONFIRMED_BY_LEAD]: 'confirmed',
};

const isNearby = (a: number, b: number) => Math.abs(a - b) <= ECHO_WINDOW_MS;

const hasMatchingSystemMessage = (
    systemMessages: TSystemMessageMoment[],
    at: number,
    predicate: (type: EMessageSystemMessageType) => boolean
) => systemMessages.some((message) => isNearby(message.at, at) && predicate(message.type));

const buildStageEvents = (
    lead: ILead,
    stages: IStageHistory[],
    leadOwnerName: string,
    messageTimes: number[],
    tourCancelTimes: number[],
    systemMessages: TSystemMessageMoment[]
): TThreadEvent[] => {
    const stageCopy = getStageCopy(lead, leadOwnerName);

    return stages
        .filter((history) => {
            const at = utcClock(history.created_at);
            const echoes = ECHO_REASONS[history.reason];
            if (echoes) {
                const causes = echoes === 'message' ? messageTimes : tourCancelTimes;
                if (causes.some((causeAt) => isNearby(causeAt, at))) {
                    return false;
                }
            }

            if (
                history.reason === 'auto_email_system_message_type' ||
                history.reason === 'auto_after_showing_inserted'
            ) {
                return !hasMatchingSystemMessage(
                    systemMessages,
                    at,
                    (type) => SYSTEM_MESSAGE_STAGE[type] === history.current_value
                );
            }

            return true;
        })
        .map((history) => {
            const origin = getStageOrigin(history.reason);
            const copy = stageCopy[history.current_value as ELeadStage] ?? getUnknownStageCopy(history.current_value);

            return {
                key: `stage-${history.id}`,
                at: utcClock(history.created_at),
                kind: 'stage' as const,
                stageValue: history.current_value,
                label: copy.label,
                detail: copy.details,
                tone: NEGATIVE_STAGES.includes(history.current_value) ? ('negative' as const) : ('positive' as const),
                origin,
                originLabel: origin && getStageOriginLabel(origin),
                originDetail: origin && getStageOriginDetails(history.reason, origin),
            };
        });
};

/**
 * The conversation as one ordered list of messages and lead-history events, so a property
 * change or a stage change is visible where it happened instead of only in the sidebar.
 */
export const buildThreadRows = ({
    lead,
    messages,
    stages,
    interests,
    tours,
    channelSwitches,
    leadOwnerName,
}: BuildArgs): TThreadRow[] => {
    const messageTimes = messages.map((message) => offsetClock(message.inserted_at));
    const systemMessages = messages.flatMap((message, index): TSystemMessageMoment[] => (
        message.system_message_type ? [{
            at: messageTimes[index],
            type: message.system_message_type,
        }] : []
    ));

    const messageRows: TThreadRow[] = messages.map((message, i) => ({
        type: 'message',
        key: message.uuid ?? `message-${i}`,
        at: messageTimes[i],
        message,
        switchedToChannel: channelSwitches[i],
    }));

    const allTourEvents = buildTourEvents(lead, tours.filter((tour) => tour.lead_uuid === lead.uuid));
    const tourCancelTimes = allTourEvents
        .filter((event) => event.tourOutcome === 'cancelled')
        .map((event) => event.at);
    const tourEvents = allTourEvents.filter((event) => !(
        event.tourOutcome && hasMatchingSystemMessage(
            systemMessages,
            event.at,
            (type) => SYSTEM_MESSAGE_TOUR_OUTCOME[type] === event.tourOutcome
        )
    ));

    const events = [
        ...buildInterestEvents(lead, interests),
        ...tourEvents,
        ...buildStageEvents(
            lead,
            stages,
            leadOwnerName,
            messageTimes,
            tourCancelTimes,
            systemMessages
        ),
    ];

    const eventRows: TThreadRow[] = events.map((event) => ({
        type: 'event',
        key: event.key,
        at: event.at,
        event,
    }));

    // Messages win ties: an event recorded off the back of an inbound message reads as its
    // consequence, so it belongs underneath.
    return [...messageRows, ...eventRows].sort((a, b) => (
        a.at === b.at ? Number(a.type === 'event') - Number(b.type === 'event') : a.at - b.at
    ));
};
